import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Image as ImageIcon,
  RefreshCw,
  StopCircle,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { feedWatchService } from '../../../services/feedWatch';
import type { CanvasRender, RenderProgress } from '../../../services/feedWatch';

interface RenderGalleryProps {
  feedId: string;
  designId: string;
  productLabels?: Map<number, string>;
  onClose: () => void;
}

function StatusBadge({ status }: { status: CanvasRender['status'] }) {
  switch (status) {
    case 'completed':
      return (
        <span className="flex items-center gap-1 text-xs font-medium text-emerald-400">
          <CheckCircle2 className="h-3 w-3" /> Done
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-center gap-1 text-xs font-medium text-destructive">
          <AlertCircle className="h-3 w-3" /> Failed
        </span>
      );
    case 'processing':
      return (
        <span className="flex items-center gap-1 text-xs font-medium text-blue-400">
          <Loader2 className="h-3 w-3 animate-spin" /> Processing
        </span>
      );
    default:
      return (
        <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          <Loader2 className="h-3 w-3" /> Queued
        </span>
      );
  }
}

function formatBytes(bytes: number | null) {
  if (bytes === null || bytes === 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export function RenderGallery({ feedId, designId, productLabels, onClose }: RenderGalleryProps) {
  const [renders, setRenders] = useState<CanvasRender[]>([]);
  const [progress, setProgress] = useState<RenderProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const lastRendersSnapshotRef = useRef<number>(0); // tracks when we last fetched full renders

  // Fetch full render list (heavy — includes all image URLs)
  const loadRenders = useCallback(async () => {
    try {
      const result = await feedWatchService.listRenders(feedId, designId);
      const list = Array.isArray(result) ? result : [];
      setRenders(list);
      lastRendersSnapshotRef.current = Date.now();
    } catch {
      // handled
    }
  }, [feedId, designId]);

  // Fetch lightweight progress (used for polling)
  const loadProgress = useCallback(async () => {
    try {
      const p = await feedWatchService.getRenderProgress(feedId, designId);
      setProgress(p);

      const isActive = p.status === 'rendering' || p.queued > 0 || p.processing > 0;

      if (isActive) {
        // Refresh full renders every 5s while active (to get new thumbnails)
        const now = Date.now();
        if (now - lastRendersSnapshotRef.current >= 5000) {
          loadRenders();
        }
      } else if (p.total > 0) {
        // Render is done — fetch final state and stop polling
        loadRenders();
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }
    } catch {
      // handled
    }
  }, [feedId, designId, loadRenders]);

  // Initial load: fetch both progress and renders
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [p] = await Promise.all([
          feedWatchService.getRenderProgress(feedId, designId),
          loadRenders(),
        ]);
        if (cancelled) return;
        setProgress(p);
      } catch {
        // handled
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    // Start polling with lightweight progress endpoint
    pollRef.current = setInterval(loadProgress, 3000);

    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [feedId, designId, loadProgress, loadRenders]);

  // Use progress data for the bar if available, fall back to counting renders
  const total = progress?.total ?? renders.length;
  const completed = progress?.completed ?? renders.filter((r) => r.status === 'completed').length;
  const failed = progress?.failed ?? renders.filter((r) => r.status === 'failed').length;
  const processing = progress?.processing ?? renders.filter((r) => r.status === 'processing').length;
  const queued = progress?.queued ?? renders.filter((r) => r.status === 'queued').length;
  const progressPct = total > 0 ? ((completed + failed) / total) * 100 : 0;
  const isActive = processing > 0 || queued > 0;

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      await feedWatchService.cancelRender(feedId, designId);
      // Refresh progress and renders after cancellation
      await Promise.all([loadProgress(), loadRenders()]);
    } catch {
      // handled
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-8">
      <div className="bg-background border-2 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold">Rendered Images</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {completed} of {total} completed
              {failed > 0 && <span className="text-destructive"> &middot; {failed} failed</span>}
              {isActive && (
                <span className="text-blue-400"> &middot; {processing} processing, {queued} queued</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isActive && (
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleCancel}
                disabled={isCancelling}
              >
                {isCancelling ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <StopCircle className="h-4 w-4 mr-1.5" />
                )}
                Cancel
              </Button>
            )}
            {!isActive && renders.length > 0 && (
              <Button variant="ghost" size="sm" onClick={loadRenders} title="Refresh renders">
                <RefreshCw className="h-4 w-4" />
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress bar */}
        {total > 0 && (
          <div className="px-5 pt-3">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  isActive ? 'bg-primary animate-pulse' : 'bg-primary'
                }`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
            </div>
          ) : renders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <ImageIcon className="h-12 w-12 mb-4" />
              <p>No renders yet. Trigger a render to generate images.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {renders.map((render) => (
                <div
                  key={render.id}
                  className="border rounded-xl overflow-hidden bg-muted/20"
                >
                  {/* Thumbnail */}
                  <div className="aspect-square bg-muted/30 flex items-center justify-center">
                    {render.status === 'completed' && render.cloudinary_url ? (
                      <img
                        src={render.cloudinary_url}
                        alt={`Product ${render.product_idx}`}
                        className="w-full h-full object-cover"
                      />
                    ) : render.status === 'failed' ? (
                      <div className="text-center p-3">
                        <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                        <p className="text-xs text-destructive">{render.error_message || 'Render failed'}</p>
                      </div>
                    ) : (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-2.5 space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground truncate max-w-[120px]">
                        {productLabels?.get(render.product_idx) ?? `Product #${render.product_idx}`}
                      </span>
                      <StatusBadge status={render.status} />
                    </div>
                    {render.file_size_bytes !== null && (
                      <p className="text-xs text-muted-foreground">{formatBytes(render.file_size_bytes)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
